import React from 'react';
import { Box } from '@mui/material';
import KpiCard from '@components/KpiCard';
import { ORDER_OFFER_STATUS_LABELS as OFFER_STATUS_FILTER_LABELS, type OrderOfferStatusKey as OfferStatusFilterKey } from '@hopdrive/feature-order-index';

/**
 * The report's KPI row. Deliberately a LIST, not three hard-coded cards: "should there be
 * more tiles" is an open question for the report's owner (a total, a declined/expired
 * rollup), and a fourth entry here is one line, not a rewrite. `highlight` marks the
 * tiles a dealer is waiting on someone for — unassigned and pending — matching how the
 * dashboard highlights its attention tile.
 */
interface KpiTileDef {
  key: OfferStatusFilterKey;
  label: string;
  highlight?: boolean;
}

const KPI_TILES: KpiTileDef[] = [
  { key: 'unassigned', label: OFFER_STATUS_FILTER_LABELS.unassigned, highlight: true },
  { key: 'offered', label: OFFER_STATUS_FILTER_LABELS.offered, highlight: true },
  { key: 'accepted', label: OFFER_STATUS_FILTER_LABELS.accepted },
];

export interface ViewAssignmentsKpisProps {
  counts: Record<OfferStatusFilterKey, number>;
  loading: boolean;
  /** The buckets currently filtering the table — the SAME state the Status picker drives. */
  selected: OfferStatusFilterKey[];
  /** Toggle one bucket in that shared state. */
  onToggle: (key: OfferStatusFilterKey) => void;
}

/**
 * The tiles are FILTERS, not just counters — the same toggle the Status multi-select
 * drives, on the same state, so the two controls can never disagree. They looked
 * clickable from day one (cards with the board's tile styling) and the amber
 * attention-highlight on the waiting pair read as "selected", so people clicked; now
 * the click does what it looks like it does, and real selection wears the primary
 * border + tint that the highlight never did.
 */
const ViewAssignmentsKpis: React.FC<ViewAssignmentsKpisProps> = ({ counts, loading, selected, onToggle }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 2.25 }}>
    {KPI_TILES.map(tile => (
      <Box key={tile.key} sx={{ flex: '1 1 180px', minWidth: 160 }}>
        <KpiCard
          title={tile.label}
          value={counts[tile.key] ?? 0}
          loading={loading}
          highlight={tile.highlight}
          selected={selected.includes(tile.key)}
          onClick={() => onToggle(tile.key)}
          aria-label={`${tile.label}: ${counts[tile.key] ?? 0}`}
        />
      </Box>
    ))}
  </Box>
);

export default ViewAssignmentsKpis;
